import { useQuery } from "@apollo/client";
import { useMemo } from "react";

import { GET_USER_GUIDE_GROUP } from "./gql";
import { getHardcodedUserGuideGroupIconAndColor } from "./mocks";
import { UserGuideGroupEnriched } from "./types";
import { collectGroupTimeToComplete, parseMinutes } from "./utils";

type UseUserGuideGroupProps = {
  id: string;
};

const useUserGuideGroup = ({ id }: UseUserGuideGroupProps) => {
  const { data, loading, error } = useQuery(GET_USER_GUIDE_GROUP, {
    variables: {
      id: id || "",
    },
    skip: !id,
  });

  const completedUserGuideIds = useMemo(() => {
    return data?.completedUserGuides.map((completedGuide) => completedGuide.guide.id) ?? [];
  }, [data?.completedUserGuides]);

  const guides = useMemo(() => {
    return data?.userGuideGroup?.chapters.flatMap((chapter) => chapter.guides) ?? [];
  }, [data?.userGuideGroup?.chapters]);

  const groupData = data?.userGuideGroup;
  const activeGuideId = data?.activeUserGuideProgress?.guide.id;
  const activeGuideCurrentStep = data?.activeUserGuideProgress?.currentStep;

  // TODO: use the actual API to get the color, icon and time to complete for the user guide
  const userGuideGroup: UserGuideGroupEnriched | undefined = useMemo(() => {
    if (!groupData) return undefined;

    return {
      id: groupData.id,
      name: groupData.name,
      description: groupData.description,
      skillLevel: groupData.skillLevel,
      requiredEntitlements: groupData.requiredEntitlements,
      chapters: groupData.chapters.map((chapter) => ({
        ...chapter,
        ...getHardcodedUserGuideGroupIconAndColor(groupData.id),
        guides: chapter.guides.map((guide) => {
          const isActive = activeGuideId === guide.id;

          return {
            ...guide,
            isCompleted: completedUserGuideIds.includes(guide.id),
            isInProgress: isActive,
            timeToComplete: parseMinutes(guide.metadata.minutesToComplete),
            hasUnmetPrerequisites:
              guide.prerequisiteGuides.all.length > guide.prerequisiteGuides.completed.length,
            currentStep: isActive ? activeGuideCurrentStep : undefined,
          };
        }),
      })),
      timeToComplete: collectGroupTimeToComplete(groupData, completedUserGuideIds),
      guidesCount: guides.length,
      guidesCompletedCount: guides.filter((guide) => completedUserGuideIds.includes(guide.id))
        .length,
    };
  }, [groupData, completedUserGuideIds, activeGuideId, activeGuideCurrentStep, guides]);

  return { userGuideGroup, loading, error };
};

export default useUserGuideGroup;
